import {
	ADMIN_TEMPLATES_AND_BLUEPRINTS,
	SPACE_SETTINGS_TEMPLATES,
} from '@confluence/named-routes/entry-points/routes.ts';
import { expVal } from '@confluence/feature-experiments';

export const IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME = 'inEditorTemplatesPanel';
export const IN_EDITOR_TEMPLATES_QUERY_OPEN = 'open';

export const IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN = 'keep_open';
export const IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED = 'auto_closed';

export const CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME = 'createdWithTemplate';

export const CONTEXT_PANEL_TAB_TARGET = 'contextPanelTabTarget';

// Tab target values (used in query params)
export const CONTEXT_PANEL_TAB_TARGET_TEMPLATES = 'templates';
export const CONTEXT_PANEL_TAB_TARGET_BLOCK = 'block';
export const CONTEXT_PANEL_TAB_TARGET_IMPORT = 'import';

// Panel mode values (used in component state)
export const PANEL_MODE_TEMPLATES = 'templates';
export const PANEL_MODE_IMPORT = 'import';

export type ContextPanelTabTarget =
	| typeof CONTEXT_PANEL_TAB_TARGET_TEMPLATES
	| typeof CONTEXT_PANEL_TAB_TARGET_BLOCK
	| typeof CONTEXT_PANEL_TAB_TARGET_IMPORT;

export type PanelMode = typeof PANEL_MODE_TEMPLATES | typeof PANEL_MODE_IMPORT;

type inEditorTemplatesPanel =
	| typeof IN_EDITOR_TEMPLATES_QUERY_OPEN
	| typeof IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN
	| typeof IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED;

// Checks if the page was created with a template by reading the 'createdWithTemplate' query param
export const getCreatedWithTemplateQueryParamValue = (getQueryParams) => {
	return getQueryParams()[CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME] === 'true';
};

// Gets the value of the 'inEditorTemplatesPanel' query param from the URL
export const getInEditorTemplatesPanelQueryParamValue = (getQueryParams) => {
	return getQueryParams()[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME];
};

// Gets the value of the 'contextPanelTabTarget' query param from the URL
export const getTemplatesPanelTabTargetQueryParamValue = (getQueryParams) => {
	return getQueryParams()[CONTEXT_PANEL_TAB_TARGET];
};

// Checks if the inEditorTemplatesPanel query param is set to 'open'
export const isInEditorTemplatesPanelQueryParamValueOpen = (getQueryParams) => {
	return (
		getInEditorTemplatesPanelQueryParamValue(getQueryParams) === IN_EDITOR_TEMPLATES_QUERY_OPEN
	);
};

// Checks if the inEditorTemplatesPanel query param is open or keep open
export const isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen = (getQueryParams) => {
	const queryParamValue = getInEditorTemplatesPanelQueryParamValue(getQueryParams);
	return (
		queryParamValue === IN_EDITOR_TEMPLATES_QUERY_OPEN ||
		queryParamValue === IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN
	);
};

// Checks if the inEditorTemplatesPanel query param is open or keep open and the templates panel mode is active
export const isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpenWithTemplatesPanelMode = (
	getQueryParams,
) => {
	const queryParamValue = getInEditorTemplatesPanelQueryParamValue(getQueryParams);
	const tabTarget = getTemplatesPanelTabTargetQueryParamValue(getQueryParams);
	return (
		(queryParamValue === IN_EDITOR_TEMPLATES_QUERY_OPEN ||
			queryParamValue === IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN) &&
		(tabTarget === CONTEXT_PANEL_TAB_TARGET_TEMPLATES ||
			tabTarget === CONTEXT_PANEL_TAB_TARGET_BLOCK)
	);
};

// Removes the inEditorTemplatesPanel query param and optionally the contextPanelTabTarget query param when cc_snippets_dogfooding is enabled
export const removeInEditorTemplatesQueryParam = (setQueryParams) => {
	const paramsToRemove: Record<string, null> = {
		[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: null,
	};

	// Only clear contextPanelTabTarget when cc_snippets_dogfooding is enabled
	if (expVal('cc_snippets_dogfooding', 'isEnabled', false)) {
		paramsToRemove[CONTEXT_PANEL_TAB_TARGET] = null;
	}

	setQueryParams(paramsToRemove, true);
};

// Sets the inEditorTemplatesPanel query param and optionally the contextPanelTabTarget query param when cc_snippets_dogfooding is enabled
export const setInEditorTemplatesPanelQueryParam = (
	setQueryParams,
	inEditorTemplatesPanel: inEditorTemplatesPanel,
	tabTarget?: ContextPanelTabTarget,
) => {
	const params: Record<string, string> = {
		[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: inEditorTemplatesPanel,
	};

	if (tabTarget && expVal('cc_snippets_dogfooding', 'isEnabled', false)) {
		params[CONTEXT_PANEL_TAB_TARGET] = tabTarget;
	}

	setQueryParams(params, true);
};

export const getTemplateListPage = (spaceKey?: string) => {
	if (spaceKey) {
		return SPACE_SETTINGS_TEMPLATES.toUrl({ spaceKey });
	}

	return ADMIN_TEMPLATES_AND_BLUEPRINTS.toUrl();
};
